import { HttpClient } from '@angular/common/http'

import { fromReadableStream } from '@rxjs-ninja/rxjs-utility'

import { Observable, concat, defer, map, mergeMap, switchMap } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import { environment } from '@flowx/designer/environment'
import { addParams } from '@flowx/shared/utils'

export interface PaginationQueryParams {
  page?: number
  size?: number
  sort?: (string | number)[]
  direction?: string
  query?: string
  keyword?: string
  filterKey?: string
  excludeSystemResources?: boolean
}

export interface IError {
  defaultMessage: string
  entityName: string
  errorKey: string
  message: string
  params: string
  status: number
  title: string
  type: string
}

const DATA_IDENTIFIER = 'data: '
export const DEFAULT_PAGINATION_PARAMS = { page: 0, size: 20 }

/** Split decoded stream text into its JSON messages, one object per emitted value. */
function parseStreamText(rawText: string): any[] {
  // Split multiple JSON messages — support parts that start with '{' or '['
  const parts = rawText
    .replaceAll(DATA_IDENTIFIER, '') // normal start identifier fix
    .trim()
    .split(/\r?\n(?=\[|\{)/) // Split by newline followed by { or [
    .filter((part) => part.trim())

  const results: any[] = []
  for (const part of parts) {
    const timestamp = new Date().toISOString()
    try {
      const parsedData = JSON.parse(part.trim())
      if (Array.isArray(parsedData)) {
        for (const item of parsedData) {
          results.push({ ...item, timestamp })
        }
      } else {
        results.push({ ...parsedData, timestamp })
      }
    } catch (error) {
      results.push({ error: 'Invalid JSON', raw: part, timestamp })
    }
  }

  return results // Emit each object separately
}

// The Angular HttpClient interceptor (WorkspacesInterceptor) attaches
// Fx-Workspace-Id and Fx-AppId so observability can group runs by tenant.
// SSE endpoints in this file use fromFetch() directly and bypass that
// interceptor, so we re-derive the same headers from the URL path here.
// Path conventions:
//   /guard/workspace/:workspaceId/projects/:projectId/...
//   /apps/:appId/...
function tenantHeadersFromLocation(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }
  const path = window.location.pathname
  const headers: Record<string, string> = {}
  const wsMatch = path.match(/\/workspace\/([0-9a-f-]{36})/i)
  if (wsMatch) {
    headers['Fx-Workspace-Id'] = wsMatch[1]
  }
  const appMatch =
    path.match(/\/projects\/([0-9a-f-]{36})/i) || path.match(/\/apps\/([0-9a-f-]{36})/i)
  if (appMatch) {
    headers['Fx-AppId'] = appMatch[1]
  }
  return headers
}

export class ApiHttpService {
  protected addParams = addParams

  constructor(
    private baseUrl: string,
    private http: HttpClient
  ) {}

  protected get = (path: string, options?: any): Observable<any> =>
    this.http.get(this.buildUrl(path), this.needBearer(path, options))

  protected head = (path: string, options?: any): Observable<any> =>
    this.http.head(this.buildUrl(path), options)

  protected post = (path: string, data: any, options?: any): Observable<any> =>
    this.http.post(this.buildUrl(path), data, this.needBearer(path, options))

  protected put = (path: string, data: any, options?: any): Observable<any> =>
    this.http.put(this.buildUrl(path), data, this.needBearer(path, options))

  protected patch = (path: string, data: any, options?: any): Observable<any> =>
    this.http.patch(this.buildUrl(path), data, this.needBearer(path, options))

  protected delete = (path: string, options?: any): Observable<any> =>
    this.http.delete(this.buildUrl(path), this.needBearer(path, options))

  protected stream = (path: string, data: any, options?: any): Observable<any> => {
    const token = localStorage.getItem('access_token')
    const signal: AbortController = new AbortController()

    return fromFetch(this.buildUrl(path), {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...tenantHeadersFromLocation(),
        ...options?.headers,
      },
    }).pipe(
      switchMap((response: Response) => {
        if (response.status >= 400) {
          throw response
        } else {
          return fromReadableStream(response.body, signal.signal)
        }
      }),
      map((uint8array: any) => new TextDecoder().decode(uint8array))
    )
  }

  protected streamArray = (
    path: string,
    data: any,
    options?: any,
    form?: FormData
  ): Observable<any> => {
    const token = localStorage.getItem('access_token')
    const signal: AbortController = new AbortController()

    let parsed_data
    let headers
    const tenantHeaders = tenantHeadersFromLocation()
    if (form instanceof FormData) {
      parsed_data = form
      headers = {
        Authorization: `Bearer ${token}`,
        ...tenantHeaders,
        ...options?.headers,
      }
    } else {
      parsed_data = JSON.stringify(data)
      headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...tenantHeaders,
        ...options?.headers,
      }
    }

    // A message can span network chunks, and so can a multi-byte character: keep one decoder per
    // stream and parse only complete lines, carrying the partial tail over to the next chunk.
    return defer(() => {
      const decoder = new TextDecoder()
      let pending = ''

      const stream$ = fromFetch(this.buildUrl(path), {
        method: 'POST',
        body: parsed_data,
        headers,
      }).pipe(
        switchMap((response: Response) => {
          if (response.status >= 400) {
            throw response
          } else {
            return fromReadableStream(response.body, signal.signal)
          }
        }),
        mergeMap((chunk: any) => {
          pending += decoder.decode(chunk, { stream: true })
          const cut = pending.lastIndexOf('\n')
          if (cut === -1) {
            return []
          }
          const rawText = pending.slice(0, cut)
          pending = pending.slice(cut + 1)
          return parseStreamText(rawText)
        })
      )

      return concat(
        stream$,
        defer(() => parseStreamText(pending + decoder.decode()))
      )
    })
  }

  protected streamFormData = (path: string, formData: FormData): Observable<any> => {
    const token = localStorage.getItem('access_token')
    const signal: AbortController = new AbortController()

    return fromFetch(this.buildUrl(path), {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
        ...tenantHeadersFromLocation(),
      },
      selector: (response: Response) => {
        if (response.status >= 400) {
          throw response
        }
        if (!response.body) {
          throw new Error('Response body is null')
        }
        return fromReadableStream(response.body, signal.signal)
      },
    }).pipe(
      map((uint8array: any) => new TextDecoder().decode(uint8array)),
      map((text: string) => {
        try {
          return JSON.parse(text)
        } catch {
          return text
        }
      })
    )
  }

  protected buildUrl(path: string): string {
    if (path) {
      if (
        (path.startsWith('ai-') ||
          path.startsWith('di-') ||
          // path.startsWith('agent-') ||
          // path.startsWith('ontology/') ||
          path.startsWith('email-connector')) &&
        !environment.production
      ) {
        return `${path}`
      } else {
        return `${this.baseUrl}/${path}`
      }
    } else {
      return `${this.baseUrl}`
    }
  }

  protected needBearer(path: string, options?: any): any {
    if (path) {
      if (
        (path.startsWith('ai-') ||
          path.startsWith('di-') ||
          // path.startsWith('agent-') ||
          path.startsWith('ontology/') ||
          path.startsWith('email-connector')) &&
        !environment.production
      ) {
        const token = localStorage.getItem('access_token')
        return {
          ...options,
          headers: {
            Authorization: `Bearer ${token}`,
            ...options?.headers,
          },
        }
      } else {
        return options
      }
    } else {
      return options
    }
  }
}
